import React, { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api";               // ⇦ único cliente HTTP

/* ---------- pequeños helpers de UI ---------- */
const Field = ({ label, ...props }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      {...props}
      className="w-full border rounded-md px-3 py-2 focus:ring-blue-600 focus:border-blue-600"
    />
  </div>
);

const Wrapper = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
    <div className="bg-white shadow-lg rounded-xl w-full max-w-md p-8">
      {children}
    </div>
  </div>
);
/* -------------------------------------------- */

export default function AuthForm({ onAuthenticated, onBack }) {
  /* ─── modo: login | register | verify ─── */
  const loc           = useLocation();
  const defaultMode   =
    new URLSearchParams(loc.search).get("mode") === "signup"
      ? "register"
      : "login";
  const [mode, setMode] = useState(defaultMode);

  /* ─── campos y estados ─── */
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [code,     setCode]     = useState("");
  const [remember, setRemember] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const [msg,     setMsg]     = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  /* ─────────────────────────────────────────
     LOGIN / REGISTER
  ───────────────────────────────────────── */
  const handleLoginOrRegister = async (e) => {
    e.preventDefault();
    setMsg("");
    setLoading(true);

    try {
      if (mode === "login") {
        /* -------- LOGIN -------- */
        const { data } = await api.post("/login", { email, password });
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("email", email);
        if (remember) localStorage.setItem("remember", "1");
        onAuthenticated?.(email);
        navigate("/app");
      } else {
        /* -------- REGISTER -------- */
        await api.post("/register", { email, password });
        setMode("verify");
      }
    } catch (err) {
      setMsg(err.response?.data?.detail || "Ocurrió un error");
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────
     VERIFICAR CÓDIGO
  ───────────────────────────────────────── */
  const handleVerify = async () => {
    setMsg("");
    setLoading(true);
    try {
      const { data } = await api.post("/verify-code", { email, code });
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("email", email);
      onAuthenticated?.(email);
      navigate("/app");
    } catch (err) {
      setMsg(err.response?.data?.detail || "Código inválido");
    } finally {
      setLoading(false);
    }
  };

  /* ==============================================================
     RENDER
  ============================================================== */

  /* ---------- Verificar código ---------- */
  if (mode === "verify") {
    return (
      <Wrapper>
        <button
          onClick={() => setMode("register")}
          className="text-sm mb-4 text-blue-600 hover:underline"
        >
          ← Volver
        </button>

        <h1 className="text-2xl font-bold text-center mb-6">
          Verifica tu correo
        </h1>
        <p className="text-sm mb-4 text-center">
          Hemos enviado un código a <strong>{email}</strong>
        </p>

        <Field
          label="Código de verificación"
          type="text"
          placeholder="123456"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />

        {/* botón con spinner */}
        <button
          onClick={handleVerify}
          disabled={code.length !== 6 || loading}
          className={`w-full rounded-full font-semibold py-2.5 transition ${
            loading
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg"
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 mr-2"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4z"
                />
              </svg>
              Procesando…
            </span>
          ) : (
            "Verificar código"
          )}
        </button>

        {msg && (
          <p className="text-sm text-red-600 mt-4 text-center">{msg}</p>
        )}
      </Wrapper>
    );
  }

  /* ---------- Login | Register ---------- */
  return (
    <Wrapper>
      {onBack && (
        <button
          onClick={onBack}
          className="text-sm mb-4 text-blue-600 hover:underline"
        >
          ← Volver
        </button>
      )}

      <h1 className="text-2xl font-bold text-center mb-6">
        {mode === "login" ? "EDBOT" : "Regístrate"}
      </h1>

      {/* pestañas */}
      <div className="flex justify-center mb-8 space-x-6 text-sm font-medium">
        <button
          onClick={() => setMode("login")}
          className={
            mode === "login"
              ? "text-blue-600"
              : "text-gray-500 hover:text-blue-600"
          }
        >
          Iniciar sesión
        </button>
        <button
          onClick={() => setMode("register")}
          className={
            mode === "register"
              ? "text-blue-600"
              : "text-gray-500 hover:text-blue-600"
          }
        >
          Registrarse
        </button>
      </div>

      <form onSubmit={handleLoginOrRegister} className="space-y-6">
        <Field
          label="Correo"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <div className="space-y-1 relative">
                            <label className="block text-sm font-medium text-gray-700">
                                   Contraseña
                            </label>

                            <input
                                   type={showPass ? "text" : "password"}
                                   className="w-full border rounded-md px-3 py-2 pr-10 focus:ring-blue-600 focus:border-blue-600"
                                   placeholder="********"
                                   minLength={6}
                                   value={password}
                                   onChange={(e) => setPassword(e.target.value)}
                                   required
                            />

                            <button
                                   type="button"
                                   onClick={() => setShowPass((v) => !v)}
                                   className="absolute inset-y-0 right-0 flex items-center px-3 focus:outline-none"
                                   tabIndex={-1}
                            >
                                   {showPass ? (
                                        <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                                   ) : (
                                     <EyeIcon className="h-5 w-5 text-gray-500" />
                                   )}
                                 </button>
                          </div>

        {mode === "login" && (
          <div className="flex items-center justify-between text-sm">
            <label className="inline-flex items-center space-x-2">
              <input
                type="checkbox"
                checked={remember}
                onChange={() => setRemember(!remember)}
                className="rounded border-gray-300 focus:ring-blue-600"
              />
              <span>Recuérdame</span>
            </label>
            <button
              type="button"
              onClick={() => navigate("/reset")}
              className="text-blue-600 hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        )}

        {/* botón principal */}
        <button
          type="submit"
          disabled={loading}
          className={`w-full rounded-full font-semibold py-2.5 transition ${
            loading
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg"
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 mr-2"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4z"
                />
              </svg>
              Procesando…
            </span>
          ) : mode === "login" ? (
            "Iniciar sesión"
          ) : (
            "Crear tu cuenta"
          )}
        </button>
      </form>

      {msg && <p className="text-sm text-red-600 mt-4 text-center">{msg}</p>}

      {/* switch */}
      <p className="text-center text-sm mt-6">
        {mode === "login" ? (
          <>
            ¿No tienes una cuenta?{" "}
            <button
              onClick={() => setMode("register")}
              className="text-blue-600 font-medium hover:underline"
            >
              Regístrate
            </button>
          </>
        ) : (
          <>
            ¿Ya estás registrado?{" "}
            <button
              onClick={() => setMode("login")}
              className="text-blue-600 font-medium hover:underline"
            >
              Ingresa
            </button>
          </>
        )}
      </p>
    </Wrapper>
  );
}
